import { TripContext } from 'contexts'
import { CatchWeightOptions } from 'enums'
// items on other customer order
import React, { FC, useContext } from 'react'
import { OrderType, ReconcileReasonType, Temperature, VerifiedMethod } from 'services/enums'
import { CatchWeight, CustomerPo, LicensePlate, LicensePlateItem, LineItem, Order, OrderItem, OrderItemChange, OrderLicensePlate, ReconcileReason, RefusalReason, VerifyMultipleItemsReason } from 'services/types'
import { CalculateDeliveredQuantityResult } from 'types/calculateDeliveredQuantityResult'
import { useLicensePlateService } from './useLicensePlateService'
import { useReturnItemService } from './useReturnItemService'

export const useOrderItemService = () => {

    const { verifyLpsByIds } = useLicensePlateService()
    const { isValidReturnSticker } = useReturnItemService()

    const tripContext = useContext(TripContext)

    const verifyItemsByTemps = (stopId: number, temps: Temperature[], reason: VerifyMultipleItemsReason, reasonComment?: string): number => {
        let items = getOrderItemsByTemps(stopId, temps)
        const verifiedItemCount: number = massVerifyItems(items, reason, reasonComment, stopId)
        return verifiedItemCount
    }

    const verifyItem = (item: OrderItem, method: VerifiedMethod, reason?: VerifyMultipleItemsReason, reasonComment?: string): void => {
        verifyItems([item], method, reason, reasonComment)
    }

    const verifyItems = (orderItems: OrderItem[], method: VerifiedMethod, reason?: VerifyMultipleItemsReason, reasonComment?: string): void => {
        const notVerifiedItems = orderItems.filter(item => item.verifiedMethod == null)
        let updatedItems = notVerifiedItems.map(item => {
            return {
                ...item,
                verifiedMethod: method,
                verifiedMultipleReasonCode: reason?.id ?? null,
                verifiedMultipleReasonComment: reasonComment ?? null,
                verifiedTime: new Date()
            }
        })

        tripContext.orderItems.bulkPut(updatedItems)
        const order: Order[] = tripContext.orders.filter(o => orderItems.some(orderItem => orderItem.orderId === o.id))
        order.forEach(o => {
            const isStcStop = tripContext.stops.find(s => s.id === o.stopId)?.orderTypes?.some(item => item == OrderType.SubjectToCount)
            isStcStop == false && tripContext.orderLicensePlates.filter(olp => o.id === olp.orderId)
                .map(olp => checkForLastVerifiedItemInLP(olp.licensePlateId, method, reason, reasonComment))
        })
    }

    const unVerifyItems = (orderItems: OrderItem[]): void => {
        const items: OrderItem[] = tripContext.orderItems.filter(oi => orderItems.some(i => i.id == oi.id))

        let updatedItems = items.map(item => {
            return {
                ...item,
                verifiedMethod: null,
                verifiedMultipleReasonCode: null,
                verifiedMultipleReasonComment: null,
                verifiedTime: null
            }
        })

        tripContext.orderItems.bulkPut(updatedItems)
    }

    const checkForLastVerifiedItemInLP = (licensePlateId: number, method: VerifiedMethod, reason?: VerifyMultipleItemsReason, reasonComment?: string): void => {
        const orderLicensePlates: OrderLicensePlate[] = tripContext.orderLicensePlates.filter(olp => olp.licensePlateId === licensePlateId)
        const licensePlateItems: LicensePlateItem[] = tripContext.licensePlateItems.filter(lpi => lpi.licensePlateId === licensePlateId)
        const licencePlateOrderItems: OrderItem[] = tripContext.orderItems.filter(oi => licensePlateItems.some(lpi => lpi.lineItemId === oi.lineItemId))
        const orderItems: OrderItem[] = licencePlateOrderItems.filter(item => orderLicensePlates.some(olp => olp.orderId === item.orderId))
        const itemsNotVerified = orderItems.filter(oi => oi.verifiedMethod == null)
        let lpIdsToVerify: number[] = []
        if (itemsNotVerified.length === 0) {
            lpIdsToVerify.push(licensePlateId)
            verifyLpsByIds(lpIdsToVerify, method, reason, reasonComment)
        }
    }

    const refuseItemsByTemps = (stopId: number, temps: Temperature[], reconcileReason: ReconcileReason, refusalReason?: RefusalReason, refusalComment?: string): number => {
        let items = getOrderItemsByTemps(stopId, temps)
        massRefuseItems(items, reconcileReason, refusalReason, refusalComment)
        return items.length;
    }

    const refuseItemsByPos = (stopId: number, pos: CustomerPo[], reconcileReason: ReconcileReason, refusalReason?: RefusalReason, refusalComment?: string): number => {
        let items = getOrderItemsByPos(stopId, pos)
        massRefuseItems(items, reconcileReason, refusalReason, refusalComment)
        return items.length;
    }

    //private
    const massVerifyItems = (orderItems: OrderItem[], reason: VerifyMultipleItemsReason, reasonComment?: string, stopId?: number): number => {
        const notVerifiedItems = orderItems.filter(item => item.verifiedMethod == null)
        const stcItems = orderItems.filter(item => item.verifiedMethod == VerifiedMethod.SubjectToCount)
        const notVerifedCatchWeights = tripContext.catchWeights.filter(cw => notVerifiedItems.some(nvi => nvi.id === cw.orderItemId) || stcItems.some(oi => oi.id === cw.orderItemId) && cw.isVerified === false)
        let updatedItems = notVerifiedItems.map(item => {
            return {
                ...item,
                verifiedMethod: VerifiedMethod.VerifiedMultiple,
                verifiedMultipleReasonCode: reason.id,
                verifiedMultipleReasonComment: reasonComment ?? null,
                countedQuantity: item.shippedQuantity,
                verifiedTime: new Date()
            }
        })

        const catchWeights = notVerifedCatchWeights.map(item => {
            return {
                ...item,
                isVerified: true,
                adjustedWeight: item.shippedWeight
            }
        })

        tripContext.orderItems.bulkPut(updatedItems)
        tripContext.catchWeights.bulkPut(catchWeights)
        tripContext.orderLicensePlates.filter(olp => orderItems.some(orderItem => orderItem.orderId === olp.orderId))
            .map(olp => checkForLastVerifiedItemInLP(olp.licensePlateId, VerifiedMethod.VerifiedMultiple, reason, reasonComment))
        return notVerifiedItems.length + notVerifedCatchWeights.length
    }

    const massRefuseItems = (orderItems: OrderItem[], reconcileReason: ReconcileReason, refusalReason?: RefusalReason, refusalComment?: string): void => {

        const stcItems = orderItems.filter(item => item.verifiedMethod == VerifiedMethod.SubjectToCount)
        const items = orderItems.length > 0 ? orderItems : stcItems
        const catchWeights = tripContext.catchWeights.filter(cw => items.some(items => items.id === cw.orderItemId))
        const orderItemChanges = tripContext.orderItemChanges.filter(oic => items.some(items => items.id === oic.orderItemId)) ?? []
        const changeEntries: OrderItemChange[] = []
        let removeEntries: number[] = []
        let id: number = new Date().getTime()

        items.forEach(orderItem => {
            const changes = orderItemChanges.filter(oic => oic.orderItemId == orderItem.id) ?? []
            if (changes.length > 0) {
                removeEntries = removeEntries.concat(changes.map(c => c.id!));
            }
            changeEntries.push({
                id: id,
                orderItemId: orderItem.id,
                reconcileReasonId: reconcileReason.id,
                quantity: orderItem.shippedQuantity,
                refusalChoiceId: (refusalReason && refusalReason.id) ?? null,
                refusalComment: refusalComment ?? null,
                noReturnStickerReason: 'Mass Refuse',
                returnSticker: null,
                codeDate: new Date(),
                reconcileReason: reconcileReason,
                isChangeApproved: null
            })
            id++
        })

        const itemChanges = items.map(orderItem => {
            return {
                ...orderItem,
                countedQuantity: reconcileReason.id == ReconcileReasonType.Shortage ? 0 : orderItem.shippedQuantity,
                verifiedMethod: VerifiedMethod.MassRefusal,
                verifiedTime: new Date(),
                refusedQuantity: orderItem.shippedQuantity
            }
        })

        const catchWeightsWithRefusal = catchWeights.map(item => {
            return {
                ...item,
                isVerified: true,
                reconcileReasonId: reconcileReason.id,
                refusalChoiceId: (refusalReason && refusalReason.id) ?? null,
                refusalComment: refusalComment ?? null,
                noReturnStickerReason: 'Mass Refuse',
                returnSticker: null,
                adjustedWeight: 0
            }
        })

        tripContext.orderItemChanges.bulkPut(changeEntries)
        removeEntries.length > 0 && tripContext.orderItemChanges.bulkRemove(removeEntries)
        tripContext.orderItems.bulkPut(itemChanges)
        tripContext.catchWeights.bulkPut(catchWeightsWithRefusal)
        tripContext.orderLicensePlates.filter(olp => orderItems.some(orderItem => orderItem.orderId === olp.orderId))
            .map(olp => checkForLastVerifiedItemInLP(olp.licensePlateId, VerifiedMethod.MassRefusal, reconcileReason, refusalComment))
    }

    const getOrderItemsByTemps = (stopId: number, temps: Temperature[]): OrderItem[] => {
        const orders: Order[] = tripContext.orders.filter(order => order.stopId == stopId)
        const stopItems: OrderItem[] = tripContext.orderItems.filter(item => orders.some(order => order.id == item.orderId))
        return stopItems.filter(item => tripContext.lineItems.some(li => li.id === item.lineItemId && temps.includes(li.temperature)))
    }

    const getOrderItemsByPos = (stopId: number, pos: CustomerPo[]): OrderItem[] => {
        const orders: Order[] = tripContext.orders.filter(order => order.stopId == stopId)
        const stopItems: OrderItem[] = tripContext.orderItems.filter(item => orders.some(order => order.id == item.orderId))
        return stopItems.filter(item => pos.some(po => po.id === item.customerPoNumber))
    }

    const getOrderItemsByLps = (lps: LicensePlate[]): OrderItem[] => {
        const lpOrders: OrderLicensePlate[] = tripContext.orderLicensePlates.filter(orderLp => lps.some(lp => lp.id == orderLp.licensePlateId))
        const lpItems: LicensePlateItem[] = tripContext.licensePlateItems.filter(lpi => lps.some(lp => lp.id === lpi.licensePlateId))
        const orderIds: number[] = lpOrders.map(lpo => lpo.orderId)
        const lpOrderItems: OrderItem[] = tripContext.orderItems.filter(oi => lpItems.some(lpItem => lpItem.lineItemId === oi.lineItemId)
            && orderIds.includes(oi.orderId))
        return lpOrderItems
    }

    const updateCatchWeightItem = async (catchWeight: CatchWeight, catchWeightOption: CatchWeightOptions): Promise<void> => {
        switch (catchWeightOption) {
            case CatchWeightOptions.Add:
                tripContext.catchWeights.put(catchWeight)
                break;
            case CatchWeightOptions.Undo:
                tripContext.catchWeights.remove(catchWeight.id)
                break;
        }
    }

    const updateOrderItemChange = (orderItemChanges: OrderItemChange): void => {
        if (orderItemChanges.quantity <= 0)
            tripContext.orderItemChanges.remove(orderItemChanges.id!)
        else
            tripContext.orderItemChanges.put(orderItemChanges)
    }

    const approveOrderItemChange = (orderItem: OrderItem) => {
        const orderItemChanges: OrderItemChange[] = tripContext.orderItemChanges.filter(change => change.orderItemId == orderItem.id)
        orderItemChanges.forEach(change => {
            change.isChangeApproved = true
            updateOrderItemChange(change)
        })
    }

    const resetOrderItemChangeApproval = (orderItem: OrderItem) => {
        const approvedOrderItemChanges: OrderItemChange[] = tripContext.orderItemChanges.filter(change => change.orderItemId == orderItem.id && change.isChangeApproved == true)
        approvedOrderItemChanges.forEach(change => {
            change.isChangeApproved = false
            updateOrderItemChange(change)
        })    
    }

    const areOrderItemChangesComplete = (items: OrderItemChange[]): boolean => {
        let complete = true
        items.forEach(item => {
            const isRefusalRequired: boolean = item.reconcileReason?.isRefusalReasonRequired ?? false
            const isRefusalChosen: boolean = item.refusalChoiceId != null
            const isReturnComplete: boolean = isValidReturnSticker(item.returnSticker) || item.noReturnStickerReason != null

            //let uploadedImages = tripContext.uploadedImages.filter(x => x.orderItemChangeId == item.id) //TODO: Re-enable this when file uploads are ready
            //const hasPictures: boolean = item.reconcileReason?.photosRequired ? uploadedImages.length > 0 : true //TODO: Re-enable this when file uploads are ready
            
            const isComplete: boolean = (!isRefusalRequired || (isRefusalRequired && isRefusalChosen && isReturnComplete))// && hasPictures TODO: Re-enable this when file uploads are ready
            if (!isComplete) complete = false
        })
        return complete
    }

    const isOrderItemChangeComplete = (item: OrderItemChange): boolean => {
        return areOrderItemChangesComplete([item])
    }

    const isOrderItemComplete = (item: OrderItem): boolean => {
        let isComplete: boolean = false
        const incompleteChanges = tripContext.orderItemChanges.filter(oic => oic.orderItemId == item?.id && !isOrderItemChangeComplete(oic))
        if(item != null && item.verifiedMethod != null && incompleteChanges.length == 0) {
            isComplete = true
        }
        return isComplete && catchWeightsAreVerified(item);
    }

    const catchWeightsAreVerified = (item: OrderItem): boolean => {
        const catchWeights: CatchWeight[] = tripContext.catchWeights.filter(weight => weight.orderItemId == item.id)

        if (catchWeights.length == 0)
            return true

        return catchWeights.every(x => x.isVerified)
    }

    const deleteOrderItems = (orderItemIds: number[]): void => {
        const catchWeights: CatchWeight[] = tripContext.catchWeights.filter(cw => orderItemIds.includes(cw.orderItemId))
        const changes: OrderItemChange[] = tripContext.orderItemChanges.filter(change => orderItemIds.includes(change.orderItemId))

        tripContext.orderItems.bulkRemove(orderItemIds),
            tripContext.orderItemChanges.bulkRemove(changes.map(change => change.id!)),
            tripContext.catchWeights.bulkRemove(catchWeights.map(cw => cw.id))
    }

    const setCatchWeights = (catchWeights: CatchWeight[]): void => {
        let items: CatchWeight[] = []
        catchWeights.forEach(item => {
            let catchWeight: CatchWeight = { ...item, catchWeightOptions: item.isVerified ? CatchWeightOptions.Verified : CatchWeightOptions.NotVerified }
            items = [...items, catchWeight]
        });
        tripContext.catchWeights.bulkPut(items)
    }

    const formatChange = (change: OrderItemChange): [string, number][] => { //[description, quantity], could be multiple if there are not enough overages left to cover refusal
        let formattedChanges = [] as [string, number][]
        const recReasons: ReconcileReason[] = tripContext.reconcileReasons.toArray() ?? [];
        const changes = tripContext.orderItemChanges.filter(oic => oic.orderItemId === change.orderItemId)

        const overageCount: number = changes.find(item => item.reconcileReasonId == ReconcileReasonType.OverageKept)?.quantity ?? 0
        const refusalCount: number = changes.filter(item => recReasons.some(reason => reason.id == item.reconcileReasonId && reason.isRefusal))
            .reduce((sum, nextChange) => sum + nextChange.quantity, 0)
        const netOverage: number = overageCount - refusalCount

        const changeReason: ReconcileReason | undefined = recReasons.find(reason => reason.id === change.reconcileReasonId)
        const changeDescription: string = changeReason?.description ?? 'Unknown'
        const changeQuantity: number = change.quantity

        if (changeReason?.id === ReconcileReasonType.OverageKept) {
            formattedChanges.push([changeDescription, netOverage])
        }
        else {
            let overagesRemaining: number = overageCount

            for (let i = 0; i < changes.length; i++) {
                const itemChange = changes[i]
                if (itemChange.id === change.id) {
                    const haveEnoughOverages = overagesRemaining >= changeQuantity
                    const adjustedDescription = overagesRemaining > 0 ? `Overage - ${changeDescription}` : changeDescription
                    const adjustedQuantity = haveEnoughOverages ? changeQuantity : overagesRemaining
                    formattedChanges.push([adjustedDescription, adjustedQuantity])
                    if (!haveEnoughOverages) {
                        formattedChanges.push([changeDescription, changeQuantity - overagesRemaining])
                    }

                    overagesRemaining -= changeQuantity
                    break
                }
                else if (itemChange.reconcileReasonId !== ReconcileReasonType.OverageKept && overagesRemaining > 0) {
                    overagesRemaining -= itemChange.quantity
                }
            }
        }

        return formattedChanges
    }

    const calculatedeliveredQuantity = (item: OrderItem, deliveredQuantityDry: number,
        deliveredQuantityFrozen: number, deliveredQuantityRefrigerated: number): CalculateDeliveredQuantityResult => {
        const lineItems: LineItem[] = tripContext.lineItems.toArray() ?? []
        const changes: OrderItemChange[] = tripContext.orderItemChanges.toArray() ?? []

        let orderItemChangeQuantity: number = 0
        let shippedQuantity: number = 0
        let itemChanges = changes.filter(oic => oic.orderItemId == item.id)
        itemChanges?.forEach(change => {
            if (change.reconcileReasonId != ReconcileReasonType.OverageKept)
                orderItemChangeQuantity += change.quantity
        })
        
        const catchWeightItems: CatchWeight[] = tripContext.catchWeights.toArray() ?? []
        let catchWeightItemChanges = catchWeightItems.filter(oic => oic.orderItemId == item.id)
        catchWeightItemChanges?.forEach(change => {
            if (change.reconcileReasonId != null
                && change.reconcileReasonId != ReconcileReasonType.OverageKept
                && change.reconcileReasonId != ReconcileReasonType.EditedCatchWeight)
                orderItemChangeQuantity += 1
        })

        shippedQuantity = item.shippedQuantity > item.countedQuantity ? item.shippedQuantity : item.countedQuantity
        let lineItem = lineItems.find(l => l.id == item.lineItemId)

        switch (lineItem?.temperature) {
            case Temperature.Dry: deliveredQuantityDry += shippedQuantity - orderItemChangeQuantity; break;
            case Temperature.Frozen: deliveredQuantityFrozen += shippedQuantity - orderItemChangeQuantity; break;
            case Temperature.Refrigerated: deliveredQuantityRefrigerated += shippedQuantity - orderItemChangeQuantity; break;
        }

        return {
            deliveredQuantityDry: deliveredQuantityDry,
            deliveredQuantityFrozen: deliveredQuantityFrozen,
            deliveredQuantityRefrigerated: deliveredQuantityRefrigerated
        }
    }

    // same item on other customer order
    const getOtherDeliveriesWithItem = (stopId: number | undefined, tripId: number | undefined, lineItemId: number | undefined): string[] => {
        const orders: Order[] = tripContext.orders.filter(o => o.stopId == stopId)
        let items: OrderItem[] = []
    
        orders.forEach(order => {
            items = [...items, ...tripContext.orderItems.filter(oi => oi.orderId == order.id && oi.lineItemId == lineItemId)]
        })

        const otherCompaniesWithTheSameOrderItem: string[] = [];

        let otherOrderItems = tripContext.orderItems.filter(x =>
            x.lineItemId == lineItemId &&
            !items.map(x => x.id).includes(x.id));

        if (otherOrderItems.length > 0) {
            for(let otherOrderItem of otherOrderItems) {
                var order = tripContext.orders.find(x => x.id === otherOrderItem.orderId);
                var stop = tripContext.stops.find(x => x.id === order?.stopId && x.tripId === tripId);
        
                if (stop === null || stop === undefined)
                    continue;
        
                var company = tripContext.companies.find(x => x.id === stop?.companyId);
        
                otherCompaniesWithTheSameOrderItem.push(`${company?.name} (${otherOrderItem.shippedQuantity})`)
                
            }
        }

        return otherCompaniesWithTheSameOrderItem
    }



    return {
        verifyItemsByTemps: verifyItemsByTemps,
        verifyItem: verifyItem,
        verifyItems: verifyItems,
        refuseItemsByTemps: refuseItemsByTemps,
        refuseItemsByPos: refuseItemsByPos,
        updateCatchWeightItem: updateCatchWeightItem,
        getOrderItemsByPos: getOrderItemsByPos,
        updateOrderItemChange: updateOrderItemChange,
        isOrderItemChangeComplete: isOrderItemChangeComplete,
        isOrderItemComplete: isOrderItemComplete,
        areOrderItemChangesComplete: areOrderItemChangesComplete,
        deleteOrderItems: deleteOrderItems,
        setCatchWeights: setCatchWeights,
        formatChange: formatChange,
        unVerifyItems: unVerifyItems,
        calculatedeliveredQuantity: calculatedeliveredQuantity,
        approveOrderItemChange: approveOrderItemChange,
        resetOrderItemChangeApproval: resetOrderItemChangeApproval,
        getOtherDeliveriesWithItem: getOtherDeliveriesWithItem
    }
}
